import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, Copy, MailWarning, Send } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import logoMark from '../../assets/ardealy-mark.svg';
import { useAuthContext } from '../../common/hooks/use-auth-context.hook';
import { InstallCheck, SignupStatus, useSignupApi } from '../../common/hooks/use-signup-api.hook';
import { INSTALL_POLL_MS, KNOWN_REASONS, inReview, isActivated, isBlocked, reasonKey } from './activation';
import { BookDemoDialog, BookDemoPrompt } from './BookDemoDialog';
import { BlockedScreen, ReviewScreen } from './ReviewScreens';
import { INSTALL_PLATFORMS, InstallPlatformId, platformTab } from './install-platforms';
import { readSignupSite } from './signup-storage';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type CheckView = { kind: 'waiting' } | { kind: 'checking' } | { kind: 'not_found'; reason: string | null } | { kind: 'throttled' };

/** The snippet the API gives, or the standard one built from the alias. */
export const snippetFor = (status: SignupStatus) =>
    status.snippet || (status.alias ? `<script src="https://ardealy.com/sdk/ardealy-sdk-v2.js" data-vendor="${status.alias}" async></script>` : '');

const Section = ({ n, title, children, className }: { n: number; title: string; children: React.ReactNode; className?: string }) => (
    <section className={cn('rounded-xl border bg-card p-5', className)}>
        <h2 className="flex items-center gap-2.5 text-sm font-medium">
            <span className="flex size-5 items-center justify-center rounded-full border text-[11px] text-muted-foreground">{n}</span>
            {title}
        </h2>
        <div className="mt-4">{children}</div>
    </section>
);

/**
 * "Connect your website" — the one step between a self-serve signup and the app.
 *
 * Nothing that costs money runs for an account until its email is confirmed
 * and the Ardealy snippet has been seen on its website (docs/plg-free-plan.md).
 * This screen gets both done: the snippet with a copy button, where to paste
 * it on each site builder, a check that runs by itself every 15 s while the
 * tab is open, and a way to hand the job to whoever edits the site.
 */
export const ActivationGate = ({
    status,
    onStatus,
    onContinue,
}: {
    status: SignupStatus;
    onStatus: (status: SignupStatus) => void;
    onContinue: () => void;
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const reduce = useReducedMotion();
    const api = useSignupApi();
    const { user, logout } = useAuthContext() as any;
    const email: string = user?.email || user?.username || '';
    const [demoOpen, setDemoOpen] = useState(false);

    const [platform, setPlatform] = useState<InstallPlatformId>(() => platformTab(readSignupSite()?.platform));
    const [copied, setCopied] = useState(false);
    const [check, setCheck] = useState<CheckView>(() =>
        status.lastCheck && !status.lastCheck.found ? { kind: 'not_found', reason: reasonKey(status.lastCheck.reason) } : { kind: 'waiting' },
    );
    const [resend, setResend] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
    const [devEmail, setDevEmail] = useState('');
    const [dev, setDev] = useState<{ state: 'idle' | 'sending' | 'sent' | 'invalid' | 'failed' | 'too_many'; to?: string }>({ state: 'idle' });
    // Connected during this visit: that is when the small celebration plays.
    const [justConnected, setJustConnected] = useState(false);

    const busy = useRef(false);
    /*
     * "Not found yet" only after someone has asked (or a check had already
     * failed before this visit). The automatic checks that run before the
     * vendor has pasted anything stay quiet, rather than greeting a new
     * account with a failure.
     */
    const spoken = useRef(Boolean(status.lastCheck && !status.lastCheck.found));
    const statusRef = useRef(status);
    statusRef.current = status;

    const domain = status.domain || '';
    const snippet = snippetFor(status);
    const connected = Boolean(status.sdkConnected);
    const done = isActivated(status);
    // Both steps are done; the decision is a person's (docs/plg-review.md).
    const reviewing = inReview(status);
    const blocked = isBlocked(status);

    const applyStatus = useCallback(
        (next: SignupStatus | null | undefined) => {
            if (!next) return;
            if ((next.sdkConnected && !statusRef.current.sdkConnected) || (isActivated(next) && !isActivated(statusRef.current))) setJustConnected(true);
            onStatus(next);
        },
        [onStatus],
    );

    /** Look for the snippet now (the button), or on the timer. */
    const checkInstall = useCallback(
        async (manual: boolean) => {
            if (manual) {
                spoken.current = true;
                setCheck({ kind: 'checking' });
            }
            // A click during the automatic check waits for that one's answer.
            if (busy.current) return;
            busy.current = true;
            let result: InstallCheck | null = null;
            try {
                result = (await api.checkInstall()) || null;
            } catch (e: any) {
                busy.current = false;
                // Throttled (one check per 10 s): the automatic check says nothing about it.
                if (e?.status === 429) {
                    if (manual) setCheck({ kind: 'throttled' });
                    return;
                }
                if (spoken.current) setCheck({ kind: 'not_found', reason: 'ERROR' });
                return;
            }
            const next = await api.status().catch(() => null);
            busy.current = false;
            applyStatus(next);
            const found = result?.found ?? next?.sdkConnected ?? false;
            if (found) {
                setCheck({ kind: 'waiting' });
                if (!next) applyStatus({ ...statusRef.current, sdkConnected: true });
            } else if (spoken.current) {
                setCheck({ kind: 'not_found', reason: reasonKey(result?.reason ?? next?.lastCheck?.reason) });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [applyStatus],
    );

    /** Only re-read the status (waiting for the email link to be opened). */
    const refreshStatus = useCallback(async () => {
        const next = await api.status().catch(() => null);
        applyStatus(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applyStatus]);

    // Opening the screen looks once: the snippet may already be there (a second visit).
    useEffect(() => {
        // The review and blocked screens have nothing to look for (the review screen asks on its own).
        if (inReview(statusRef.current) || isBlocked(statusRef.current)) return;
        if (!statusRef.current.sdkConnected) checkInstall(false);
        else if (!isActivated(statusRef.current)) refreshStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The automatic check: every 15 s while this tab is visible, and at once when it comes back.
    useEffect(() => {
        if (done || reviewing || blocked) return undefined;
        const tick = () => {
            if (document.visibilityState === 'hidden') return;
            if (statusRef.current.sdkConnected) refreshStatus();
            else checkInstall(false);
        };
        const id = window.setInterval(tick, INSTALL_POLL_MS);
        const onVisible = () => document.visibilityState === 'visible' && tick();
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [done, reviewing, blocked, checkInstall, refreshStatus]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            /* the snippet is selectable; copying by hand still works */
        }
    };

    const resendVerification = async () => {
        setResend('sending');
        try {
            await api.resendVerification();
            setResend('sent');
        } catch {
            setResend('failed');
        }
    };

    const sendToDeveloper = async () => {
        const to = devEmail.trim();
        if (!EMAIL.test(to)) {
            setDev({ state: 'invalid' });
            return;
        }
        setDev({ state: 'sending' });
        try {
            await api.sendToDeveloper(to);
            setDev({ state: 'sent', to });
            setDevEmail('');
        } catch (e: any) {
            setDev({ state: e?.status === 429 ? 'too_many' : 'failed' });
        }
    };

    const signOut = () => {
        logout();
        navigate('/login', { replace: true });
    };

    const current = INSTALL_PLATFORMS.find((p) => p.id === platform) ?? INSTALL_PLATFORMS[INSTALL_PLATFORMS.length - 1];
    const reasonText = (reason: string | null) =>
        reason === 'ERROR'
            ? t('SIGNUP.GATE.CHECK.ERROR')
            : reason && (KNOWN_REASONS as readonly string[]).includes(reason)
              ? t(`SIGNUP.GATE.REASON.${reason}`, { domain })
              : t('SIGNUP.GATE.CHECK.NOT_FOUND_BODY', { domain });

    return (
        <div className="fixed inset-0 z-[60] flex flex-col overflow-hidden bg-background" role="dialog" aria-modal aria-label={t('SIGNUP.GATE.TITLE') as string} data-testid="activation-gate">
            <header className="flex items-center gap-4 px-5 py-4 sm:px-8">
                <img src={logoMark} alt="Ardealy" className="size-7 object-contain brightness-0 dark:invert" />
                <Button variant="ghost" size="sm" className="ms-auto text-muted-foreground" onClick={signOut} data-testid="gate-sign-out">
                    {t('SIGNUP.GATE.SIGN_OUT')}
                </Button>
            </header>

            <main className="flex-1 overflow-y-auto">
                <div className="mx-auto w-full max-w-[640px] px-5 pt-6 pb-16 sm:px-8">
                    {done ? (
                        <Connected domain={domain} celebrate={!reduce && justConnected} onContinue={onContinue} />
                    ) : reviewing ? (
                        <ReviewScreen status={status} email={email} onStatus={applyStatus} onBookDemo={() => setDemoOpen(true)} />
                    ) : blocked ? (
                        <BlockedScreen status={status} onBookDemo={() => setDemoOpen(true)} />
                    ) : (
                        <>
                            <h1 className="font-heading text-[26px] leading-tight font-medium tracking-tight text-balance sm:text-[30px]">{t('SIGNUP.GATE.TITLE')}</h1>
                            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                                {t('SIGNUP.GATE.SUBTITLE', { domain: domain || t('SIGNUP.GATE.YOUR_SITE') })}
                            </p>
                            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t('SIGNUP.GATE.WHY')}</p>

                            {!status.emailVerified && (
                                <Alert className="mt-6" data-testid="gate-verify-email">
                                    <MailWarning />
                                    <AlertTitle>{t('SIGNUP.GATE.VERIFY.TITLE')}</AlertTitle>
                                    <AlertDescription>
                                        <p>{email ? t('SIGNUP.GATE.VERIFY.BODY', { email }) : t('SIGNUP.GATE.VERIFY.BODY_NO_EMAIL')}</p>
                                        {resend === 'sent' ? (
                                            <p className="mt-2 font-medium" role="status">{t('SIGNUP.GATE.VERIFY.SENT')}</p>
                                        ) : (
                                            <Button variant="outline" size="sm" className="mt-2" onClick={resendVerification} disabled={resend === 'sending'} data-testid="gate-resend">
                                                {resend === 'sending' && <Spinner />}
                                                {t('SIGNUP.GATE.VERIFY.RESEND')}
                                            </Button>
                                        )}
                                        {resend === 'failed' && <p className="mt-2 text-destructive">{t('SIGNUP.GATE.VERIFY.FAILED')}</p>}
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="mt-6 flex flex-col gap-3">
                                <Section n={1} title={t('SIGNUP.GATE.SNIPPET.TITLE')}>
                                    <div className="relative">
                                        <pre dir="ltr" className="overflow-x-auto rounded-lg border bg-muted/40 p-3 pe-24 font-mono text-xs leading-relaxed whitespace-pre-wrap break-all select-all" data-testid="gate-snippet">
                                            {snippet}
                                        </pre>
                                        <Button variant="outline" size="sm" className="absolute end-2 top-2" onClick={copy} data-testid="gate-copy">
                                            {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
                                            {copied ? t('SIGNUP.GATE.SNIPPET.COPIED') : t('SIGNUP.GATE.SNIPPET.COPY')}
                                        </Button>
                                    </div>
                                    <p className="mt-2 text-xs text-muted-foreground">{t('SIGNUP.GATE.SNIPPET.HINT')}</p>
                                </Section>

                                <Section n={2} title={t('SIGNUP.GATE.PASTE.TITLE')}>
                                    <div role="tablist" aria-label={t('SIGNUP.GATE.PASTE.TITLE') as string} className="flex flex-wrap gap-1.5">
                                        {INSTALL_PLATFORMS.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                role="tab"
                                                aria-selected={p.id === platform}
                                                onClick={() => setPlatform(p.id)}
                                                className={cn(
                                                    'rounded-full border px-3 py-1 text-xs transition-colors',
                                                    p.id === platform ? 'border-foreground text-foreground ring-[0.5px] ring-foreground' : 'text-muted-foreground hover:text-foreground',
                                                )}
                                                data-testid={`gate-platform-${p.id}`}
                                            >
                                                {t(`SIGNUP.GATE.PLATFORM.${p.key}.NAME`)}
                                            </button>
                                        ))}
                                    </div>
                                    <ol role="tabpanel" className="mt-4 flex list-none flex-col gap-2.5 text-sm" data-testid="gate-platform-steps">
                                        {Array.from({ length: current.steps }, (_, i) => (
                                            <li key={i} className="flex gap-3">
                                                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] text-muted-foreground">{i + 1}</span>
                                                <span className="leading-relaxed">{t(`SIGNUP.GATE.PLATFORM.${current.key}.STEP_${i + 1}`)}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </Section>

                                <Section n={3} title={t('SIGNUP.GATE.CHECK.TITLE')}>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <div className="min-w-0 flex-1" aria-live="polite" data-testid="gate-check-state">
                                            {connected ? (
                                                <p className="flex items-center gap-2 text-sm font-medium text-success" data-testid="gate-connected">
                                                    <Check className="size-4" />
                                                    {t('SIGNUP.GATE.CHECK.CONNECTED', { domain })}
                                                </p>
                                            ) : check.kind === 'checking' ? (
                                                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <Spinner className="size-3.5" aria-hidden />
                                                    {t('SIGNUP.GATE.CHECK.CHECKING', { domain })}
                                                </p>
                                            ) : check.kind === 'not_found' ? (
                                                <div data-testid="gate-not-found">
                                                    <p className="text-sm font-medium">{t('SIGNUP.GATE.CHECK.NOT_FOUND')}</p>
                                                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{reasonText(check.reason)}</p>
                                                </div>
                                            ) : check.kind === 'throttled' ? (
                                                <p className="text-sm text-muted-foreground">{t('SIGNUP.GATE.CHECK.THROTTLED')}</p>
                                            ) : (
                                                <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="gate-waiting">
                                                    <span className="relative flex size-2">
                                                        <span className="absolute inline-flex size-full animate-ping rounded-full bg-muted-foreground/40" />
                                                        <span className="relative inline-flex size-2 rounded-full bg-muted-foreground/60" />
                                                    </span>
                                                    {t('SIGNUP.GATE.CHECK.WAITING', { domain })}
                                                </p>
                                            )}
                                        </div>
                                        {!connected && (
                                            <Button variant="outline" onClick={() => checkInstall(true)} disabled={check.kind === 'checking'} data-testid="gate-check">
                                                {check.kind === 'checking' && <Spinner />}
                                                {t('SIGNUP.GATE.CHECK.BUTTON')}
                                            </Button>
                                        )}
                                    </div>
                                    {connected && !status.emailVerified ? (
                                        <p className="mt-2 text-xs text-muted-foreground">{t('SIGNUP.GATE.CHECK.NOW_VERIFY')}</p>
                                    ) : (
                                        !connected && <p className="mt-2 text-xs text-muted-foreground">{t('SIGNUP.GATE.CHECK.AUTO')}</p>
                                    )}
                                </Section>

                                {!connected && (
                                    <section className="rounded-xl border border-dashed p-5" data-testid="gate-developer">
                                        <h2 className="text-sm font-medium">{t('SIGNUP.GATE.DEVELOPER.TITLE')}</h2>
                                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t('SIGNUP.GATE.DEVELOPER.BODY')}</p>
                                        <form
                                            className="mt-3 flex flex-wrap items-end gap-2"
                                            noValidate
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                sendToDeveloper();
                                            }}
                                        >
                                            <div className="min-w-[220px] flex-1">
                                                <Label htmlFor="gate-dev-email" className="sr-only">
                                                    {t('SIGNUP.GATE.DEVELOPER.EMAIL')}
                                                </Label>
                                                <Input
                                                    id="gate-dev-email"
                                                    type="email"
                                                    placeholder={t('SIGNUP.GATE.DEVELOPER.PLACEHOLDER') as string}
                                                    value={devEmail}
                                                    onChange={(e) => {
                                                        setDevEmail(e.target.value);
                                                        if (dev.state !== 'sending') setDev({ state: 'idle' });
                                                    }}
                                                    aria-invalid={dev.state === 'invalid' || undefined}
                                                    data-testid="gate-dev-email"
                                                />
                                            </div>
                                            <Button type="submit" variant="outline" disabled={dev.state === 'sending'} data-testid="gate-dev-send">
                                                {dev.state === 'sending' ? <Spinner /> : <Send data-icon="inline-start" className="rtl:-scale-x-100" />}
                                                {t('SIGNUP.GATE.DEVELOPER.SEND')}
                                            </Button>
                                        </form>
                                        {dev.state === 'sent' && (
                                            <p className="mt-2 text-xs text-success" role="status" data-testid="gate-dev-sent">
                                                {t('SIGNUP.GATE.DEVELOPER.SENT', { email: dev.to })}
                                            </p>
                                        )}
                                        {dev.state === 'invalid' && <p className="mt-2 text-xs text-destructive">{t('SIGNUP.GATE.DEVELOPER.INVALID')}</p>}
                                        {dev.state === 'too_many' && <p className="mt-2 text-xs text-destructive">{t('SIGNUP.GATE.DEVELOPER.TOO_MANY')}</p>}
                                        {dev.state === 'failed' && <p className="mt-2 text-xs text-destructive">{t('SIGNUP.GATE.DEVELOPER.FAILED')}</p>}
                                    </section>
                                )}

                                {!connected && (
                                    <BookDemoPrompt text={t('SIGNUP.DEMO.PROMPT_GATE')} onClick={() => setDemoOpen(true)} className="px-1 pt-1 text-xs leading-relaxed text-muted-foreground" testId="gate-demo" />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </main>

            <BookDemoDialog
                open={demoOpen}
                onOpenChange={setDemoOpen}
                signedIn
                prefill={{
                    name: [user?.firstname, user?.lastname].filter(Boolean).join(' ') || undefined,
                    email: email || undefined,
                    company: user?.vendor?.company || user?.vendor?.name || undefined,
                    website: domain || undefined,
                }}
                defaultReason={reviewing ? 'want_walkthrough' : blocked ? 'other' : 'cant_install_now'}
                onSent={() => onStatus({ ...statusRef.current, demoRequested: true })}
            />
        </div>
    );
};

/** Both done: a small moment, then on into the app. */
const Connected = ({ domain, celebrate, onContinue }: { domain: string; celebrate: boolean; onContinue: () => void }) => {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col items-center pt-16 text-center" data-testid="gate-done">
            <div className="relative">
                {celebrate &&
                    Array.from({ length: 8 }, (_, i) => {
                        const angle = (i / 8) * Math.PI * 2;
                        return (
                            <motion.span
                                key={i}
                                aria-hidden
                                className="absolute start-1/2 top-1/2 size-1.5 rounded-full bg-success"
                                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                                animate={{ x: Math.cos(angle) * 46, y: Math.sin(angle) * 46, opacity: 0, scale: 0.4 }}
                                transition={{ duration: 0.9, delay: 0.25, ease: 'easeOut' }}
                            />
                        );
                    })}
                <motion.div
                    className="flex size-16 items-center justify-center rounded-full border-[1.5px] border-success text-success"
                    initial={celebrate ? { scale: 0.6, opacity: 0 } : false}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                >
                    <Check className="size-7" />
                </motion.div>
            </div>
            <h1 className="mt-7 font-heading text-[26px] leading-tight font-medium tracking-tight">{t('SIGNUP.GATE.DONE.TITLE')}</h1>
            <p className="mt-2.5 max-w-[440px] text-sm leading-relaxed text-muted-foreground">{t('SIGNUP.GATE.DONE.BODY', { domain })}</p>
            <Button variant="outline" size="lg" className="mt-8" onClick={onContinue} autoFocus data-testid="gate-continue">
                {t('SIGNUP.GATE.DONE.CONTINUE')}
                <ArrowRight className="rtl:rotate-180" data-icon="inline-end" />
            </Button>
        </div>
    );
};
